import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./api";

const CURRENCIES = {
  INR: { code: "INR", symbol: "₹", locale: "en-IN" },
  USD: { code: "USD", symbol: "$", locale: "en-US" },
  GBP: { code: "GBP", symbol: "£", locale: "en-GB" },
};

const KEY = "md_currency_v1";

const CurrencyCtx = createContext({ code: "INR", change: () => {}, format: (v) => `₹${v}`, options: ["INR", "USD", "GBP"] });

export const CurrencyProvider = ({ children }) => {
  const [code, setCode] = useState(() => localStorage.getItem(KEY) || "INR");
  const [rates, setRates] = useState({ usd: 0.012, gbp: 0.0095 });

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const usd = Number(r.data?.currency_rates_usd) || 0.012;
      const gbp = Number(r.data?.currency_rates_gbp) || 0.0095;
      setRates({ usd, gbp });
    }).catch(() => {});
  }, []);

  const change = useCallback((c) => {
    if (!CURRENCIES[c]) return;
    localStorage.setItem(KEY, c);
    setCode(c);
  }, []);

  const currentRate = code === "USD" ? rates.usd : code === "GBP" ? rates.gbp : 1;
  const currency = CURRENCIES[code];

  const format = useCallback((amountInr) => {
    const v = Number(amountInr || 0) * currentRate;
    const opts = { style: "currency", currency: currency.code, maximumFractionDigits: code === "INR" ? 0 : 2 };
    try { return new Intl.NumberFormat(currency.locale, opts).format(v); } catch { return `${currency.symbol}${v.toFixed(2)}`; }
  }, [currentRate, currency.code, currency.locale, currency.symbol, code]);

  const options = useMemo(() => Object.keys(CURRENCIES), []);
  const ctxValue = useMemo(
    () => ({ code, currency, change, format, options }),
    [code, currency, change, format, options]
  );

  return (
    <CurrencyCtx.Provider value={ctxValue}>
      {children}
    </CurrencyCtx.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyCtx);
